'use client';

import { Card } from '@app/app/_components/Card/Card';
import { Heading } from '@app/app/_components/Heading/Heading';
import { DatePicker } from '@app/app/_components/DatePicker/DatePicker';
import { CheckmarkCircleIcon } from '@app/app/_icons/CheckmarkCircleIcon';
import { TransportPolicyValidity } from '@app/domain/model/eshop/transport/TransportPolicyValidity';
import { PremiumType } from '@app/domain/model/eshop/transport/PremiumType';
import { addDays, addMonths, format, subDays, parseISO, isAfter, differenceInDays } from 'date-fns';
import { useTranslations } from 'next-intl';
import { useCallback, useMemo, useEffect } from 'react';

interface Props {
    insuranceDuration?: string;
    policyValidity: Nullable<TransportPolicyValidity>;
    insurancePackage: string;
    setCascoStartDate: (startDate: string) => void;
    setMtplStartDate: (startDate: string) => void;
    cascoStartDate: string;
    mtplStartDate: string;
}

export function InsuranceStartDateStep({
    insuranceDuration,
    policyValidity,
    insurancePackage,
    setCascoStartDate,
    setMtplStartDate,
    cascoStartDate,
    mtplStartDate,
}: Props) {
    const t = useTranslations();

    const hasCasco = insurancePackage === PremiumType.Casco || insurancePackage === PremiumType.CascoMtpl;
    const hasMtpl = insurancePackage === PremiumType.Mtpl || insurancePackage === PremiumType.CascoMtpl;

    const getDateInfo = useCallback(
        (policyType: string) => {
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            const oneMonthFromToday = addMonths(today, 1);
            let minAllowedDate = today;
            let maxAllowedDate = oneMonthFromToday;
            let defaultDate = today;

            if (policyValidity) {
                const policy =
                    policyType === PremiumType.Casco
                        ? policyValidity.cascoPolicyValidity
                        : policyValidity.mtplPolicyValidity;

                if (policy?.isValid && policy.validUntil) {
                    const policyEndDate = parseISO(policy.validUntil);
                    policyEndDate.setHours(0, 0, 0, 0);
                    const dayAfterPolicyEnd = addDays(policyEndDate, 1);
                    const daysUntilPolicyEnd = differenceInDays(policyEndDate, today);

                    if (daysUntilPolicyEnd >= 0 && daysUntilPolicyEnd <= 30) {
                        minAllowedDate = dayAfterPolicyEnd;
                        defaultDate = dayAfterPolicyEnd;
                        maxAllowedDate = addMonths(dayAfterPolicyEnd, 1);
                    } else if (isAfter(dayAfterPolicyEnd, today)) {
                        minAllowedDate = dayAfterPolicyEnd;
                        defaultDate = dayAfterPolicyEnd;
                        maxAllowedDate = addMonths(dayAfterPolicyEnd, 1);
                    }
                }
            }

            return {
                minDate: minAllowedDate,
                maxDate: maxAllowedDate,
                defaultDate: defaultDate,
            };
        },
        [policyValidity],
    );

    const cascoDateConfig = useMemo(() => getDateInfo(PremiumType.Casco), [getDateInfo]);
    const mtplDateConfig = useMemo(() => getDateInfo(PremiumType.Mtpl), [getDateInfo]);

    useEffect(() => {
        if (hasCasco && !cascoStartDate) {
            setCascoStartDate(format(cascoDateConfig.defaultDate, 'yyyy-MM-dd'));
        }
        if (hasMtpl && !mtplStartDate) {
            setMtplStartDate(format(mtplDateConfig.defaultDate, 'yyyy-MM-dd'));
        }
    }, [
        cascoDateConfig.defaultDate,
        mtplDateConfig.defaultDate,
        hasCasco,
        hasMtpl,
        setCascoStartDate,
        setMtplStartDate,
        cascoStartDate,
        mtplStartDate,
    ]);

    const calculateEndDate = useCallback(
        (startDate: string): Nullable<string> => {
            if (!startDate || !insuranceDuration) return null;

            const date = new Date(startDate);
            if (isNaN(date.getTime())) return null;

            const durationInMonths = parseInt(insuranceDuration, 10);
            if (isNaN(durationInMonths)) return null;

            const endDate = subDays(addMonths(date, durationInMonths), 1);
            return format(endDate, 'yyyy-MM-dd');
        },
        [insuranceDuration],
    );

    const handleCascoDateChange = useCallback(
        (date: Nullable<Date>) => {
            if (date) {
                setCascoStartDate(format(date, 'yyyy-MM-dd'));
            }
        },
        [setCascoStartDate],
    );

    const handleMtplDateChange = useCallback(
        (date: Nullable<Date>) => {
            if (date) {
                setMtplStartDate(format(date, 'yyyy-MM-dd'));
            }
        },
        [setMtplStartDate],
    );

    const renderDateInput = (
        startDateName: string,
        endDateName: string,
        labelKey: string,
        value: string,
        onChange: (date: Nullable<Date>) => void,
        minDate: Date,
        maxDate: Date,
        endDate: Nullable<string>,
    ) => (
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            <DatePicker
                key={value || 'empty'}
                name={startDateName}
                label={t(labelKey)}
                showResetButton={false}
                placeholder={t('eshop.transport.insuranceStartDatePlaceholder')}
                defaultValue={value ? parseISO(value) : undefined}
                onChange={onChange}
                minDate={minDate}
                maxDate={maxDate}
                alternateFormatting={true}
            />
            <input type="hidden" name={endDateName} value={endDate ?? ''} />

            {endDate && (
                <div className="inline-flex items-center gap-2 font-medium md:mt-7">
                    <CheckmarkCircleIcon className="size-4 text-primary-400" />
                    <span>
                        <span className="text-neutral-500">{t('eshop.transport.insuranceValidTill')}</span>
                        &nbsp;
                        <span>{endDate}</span>
                    </span>
                </div>
            )}
        </div>
    );

    return (
        <Card className="p-6">
            <div className="space-y-6">
                {hasCasco && (
                    <div>
                        <Heading level="h4" className="mb-3">
                            {t('eshop.transport.policyType.casco')}
                        </Heading>
                        {renderDateInput(
                            'cascoStartDate',
                            'cascoEndDate',
                            'eshop.transport.selectStartDateOfCasco',
                            cascoStartDate,
                            handleCascoDateChange,
                            cascoDateConfig.minDate,
                            cascoDateConfig.maxDate,
                            calculateEndDate(cascoStartDate),
                        )}
                    </div>
                )}

                {hasMtpl && (
                    <div>
                        <Heading level="h4" className="mb-3">
                            {t('eshop.transport.policyType.mtpl')}
                        </Heading>
                        {renderDateInput(
                            'mtplStartDate',
                            'mtplEndDate',
                            'eshop.transport.selectStartDateOfMTPL',
                            mtplStartDate,
                            handleMtplDateChange,
                            mtplDateConfig.minDate,
                            mtplDateConfig.maxDate,
                            calculateEndDate(mtplStartDate),
                        )}
                    </div>
                )}
            </div>
        </Card>
    );
}
